// Location view for venue information and directions

import React, { useState } from 'react'
import { useEventLocation } from '../../hooks/useSupplierTasks'
import styles from './LocationView.module.css'

interface LocationViewProps {
  eventId: string
}

interface LocationInfo {
  venue: {
    name: string
    address: string
    city: string
    state: string
    zipCode: string
    country: string
    phone?: string
    website?: string
  }
  coordinates?: {
    latitude: number
    longitude: number
  }
  loadingDock?: {
    address: string
    instructions: string
    restrictions?: string[]
    hours?: string
  }
  parking?: {
    type: 'free' | 'paid' | 'restricted'
    instructions: string
    cost?: string
  }
  security?: {
    requirements: string[]
    contactInfo?: string
  }
  specialInstructions?: string[]
  emergencyContacts?: {
    name: string
    role: string
    phone: string
    hours: string
  }[]
}

export const LocationView: React.FC<LocationViewProps> = ({ eventId }) => {
  const { data: location, isLoading, error } = useEventLocation(eventId)
  const [activeSection, setActiveSection] = useState<'overview' | 'loading' | 'parking' | 'emergency'>('overview')

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Loading location information...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorIcon}>⚠️</div>
        <h3>Unable to Load Location</h3>
        <p>We couldn't retrieve location information for this event.</p>
        <p>Please contact your event coordinator for venue details.</p>
      </div>
    )
  }

  if (!location) {
    return (
      <div className={styles.emptyContainer}>
        <div className={styles.emptyIcon}>📍</div>
        <h3>No Location Information</h3>
        <p>Location details haven't been provided for this event yet.</p>
      </div>
    )
  }

  const locationData = location as LocationInfo
  const fullAddress = `${locationData.venue.address}, ${locationData.venue.city}, ${locationData.venue.state} ${locationData.venue.zipCode}`

  const handleGetDirections = () => {
    const query = encodeURIComponent(fullAddress)
    
    // Detect device and open appropriate maps app
    const userAgent = navigator.userAgent.toLowerCase()
    if (userAgent.includes('iphone') || userAgent.includes('ipad')) {
      window.open(`maps://maps.apple.com/?q=${query}`, '_blank')
    } else if (userAgent.includes('android')) {
      window.open(`intent://maps.google.com/maps?q=${query}#Intent;scheme=https;package=com.google.android.apps.maps;end`, '_blank')
    } else {
      window.open(`https://maps.google.com/maps?q=${query}`, '_blank')
    }
  }

  const handleCall = (phone: string) => {
    window.location.href = `tel:${phone}`
  }

  return (
    <div className={styles.container}>
      {/* Header with venue info */}
      <div className={styles.header}>
        <div className={styles.venueInfo}>
          <h2 className={styles.venueName}>{locationData.venue.name}</h2>
          <div className={styles.venueAddress}>
            <span className={styles.addressIcon}>📍</span>
            <div className={styles.addressText}>
              <div>{locationData.venue.address}</div>
              <div>{locationData.venue.city}, {locationData.venue.state} {locationData.venue.zipCode}</div>
            </div>
          </div>
        </div>

        <div className={styles.actions}>
          <button 
            className={styles.directionsButton}
            onClick={handleGetDirections}
          >
            <span className={styles.buttonIcon}>🧭</span>
            Get Directions
          </button>
          {locationData.venue.phone && (
            <button 
              className={styles.callButton}
              onClick={() => handleCall(locationData.venue.phone!)}
            >
              <span className={styles.buttonIcon}>📞</span>
              Call Venue
            </button>
          )}
        </div>
      </div>

      {/* Navigation tabs */}
      <div className={styles.tabContainer}>
        <button
          className={`${styles.tab} ${activeSection === 'overview' ? styles.activeTab : ''}`}
          onClick={() => setActiveSection('overview')}
        >
          <span className={styles.tabIcon}>ℹ️</span>
          Overview
        </button>
        {locationData.loadingDock && (
          <button
            className={`${styles.tab} ${activeSection === 'loading' ? styles.activeTab : ''}`}
            onClick={() => setActiveSection('loading')}
          >
            <span className={styles.tabIcon}>🚚</span>
            Loading Dock
          </button>
        )}
        {locationData.parking && (
          <button
            className={`${styles.tab} ${activeSection === 'parking' ? styles.activeTab : ''}`}
            onClick={() => setActiveSection('parking')}
          >
            <span className={styles.tabIcon}>🅿️</span>
            Parking
          </button>
        )}
        {locationData.emergencyContacts && (
          <button
            className={`${styles.tab} ${activeSection === 'emergency' ? styles.activeTab : ''}`}
            onClick={() => setActiveSection('emergency')}
          >
            <span className={styles.tabIcon}>🚨</span>
            Emergency
          </button>
        )}
      </div>

      {/* Tab content */}
      <div className={styles.content}>
        {activeSection === 'overview' && (
          <div className={styles.overviewSection}>
            <div className={styles.infoGrid}>
              {/* Contact info */}
              <div className={styles.infoCard}>
                <h3 className={styles.cardTitle}>
                  <span className={styles.cardIcon}>📞</span>
                  Contact Information
                </h3>
                <div className={styles.cardContent}>
                  {locationData.venue.phone && (
                    <div className={styles.contactItem}>
                      <strong>Phone:</strong>
                      <a 
                        href={`tel:${locationData.venue.phone}`}
                        className={styles.phoneLink}
                      >
                        {locationData.venue.phone}
                      </a>
                    </div>
                  )}
                  {locationData.venue.website && (
                    <div className={styles.contactItem}>
                      <strong>Website:</strong>
                      <a 
                        href={locationData.venue.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.websiteLink}
                      >
                        Visit Website
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Security requirements */}
              {locationData.security && (
                <div className={styles.infoCard}>
                  <h3 className={styles.cardTitle}>
                    <span className={styles.cardIcon}>🔒</span>
                    Security Requirements
                  </h3>
                  <div className={styles.cardContent}>
                    <ul className={styles.requirementsList}>
                      {locationData.security.requirements.map((req, index) => (
                        <li key={index}>{req}</li>
                      ))}
                    </ul>
                    {locationData.security.contactInfo && (
                      <div className={styles.securityContact}>
                        <strong>Security Contact:</strong> {locationData.security.contactInfo}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Special instructions */}
            {locationData.specialInstructions && locationData.specialInstructions.length > 0 && (
              <div className={styles.instructionsCard}>
                <h3 className={styles.cardTitle}>
                  <span className={styles.cardIcon}>⚠️</span>
                  Important Instructions
                </h3>
                <div className={styles.cardContent}>
                  <ul className={styles.instructionsList}>
                    {locationData.specialInstructions.map((instruction, index) => (
                      <li key={index}>{instruction}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}

        {activeSection === 'loading' && locationData.loadingDock && (
          <div className={styles.loadingSection}>
            <div className={styles.sectionCard}>
              <h3 className={styles.cardTitle}>
                <span className={styles.cardIcon}>🚚</span>
                Loading Dock Information
              </h3>
              <div className={styles.cardContent}>
                <div className={styles.loadingAddress}>
                  <strong>Loading Dock Address:</strong>
                  <div className={styles.addressBlock}>
                    {locationData.loadingDock.address}
                  </div>
                </div>

                <div className={styles.loadingInstructions}>
                  <strong>Instructions:</strong>
                  <p>{locationData.loadingDock.instructions}</p>
                </div>

                {locationData.loadingDock.hours && (
                  <div className={styles.loadingHours}>
                    <strong>Loading Hours:</strong>
                    <p>{locationData.loadingDock.hours}</p>
                  </div>
                )}

                {locationData.loadingDock.restrictions && locationData.loadingDock.restrictions.length > 0 && (
                  <div className={styles.loadingRestrictions}>
                    <strong>Restrictions:</strong>
                    <ul>
                      {locationData.loadingDock.restrictions.map((restriction, index) => (
                        <li key={index}>{restriction}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeSection === 'parking' && locationData.parking && (
          <div className={styles.parkingSection}>
            <div className={styles.sectionCard}>
              <h3 className={styles.cardTitle}>
                <span className={styles.cardIcon}>🅿️</span>
                Parking Information
              </h3>
              <div className={styles.cardContent}>
                <div className={styles.parkingType}>
                  <strong>Parking Type:</strong>
                  <span className={`${styles.parkingBadge} ${styles[locationData.parking.type]}`}>
                    {locationData.parking.type.charAt(0).toUpperCase() + locationData.parking.type.slice(1)}
                  </span>
                </div>

                {locationData.parking.cost && (
                  <div className={styles.parkingCost}>
                    <strong>Cost:</strong> {locationData.parking.cost}
                  </div>
                )}

                <div className={styles.parkingInstructions}>
                  <strong>Instructions:</strong>
                  <p>{locationData.parking.instructions}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'emergency' && locationData.emergencyContacts && (
          <div className={styles.emergencySection}>
            <div className={styles.sectionCard}>
              <h3 className={styles.cardTitle}>
                <span className={styles.cardIcon}>🚨</span>
                Emergency Contacts
              </h3>
              <div className={styles.cardContent}>
                <div className={styles.emergencyList}>
                  {locationData.emergencyContacts.map((contact, index) => (
                    <div key={index} className={styles.emergencyContact}>
                      <div className={styles.contactHeader}>
                        <strong>{contact.name}</strong>
                        <span className={styles.contactRole}>({contact.role})</span>
                      </div>
                      <div className={styles.contactDetails}>
                        <a 
                          href={`tel:${contact.phone}`}
                          className={styles.emergencyPhone}
                        >
                          {contact.phone}
                        </a>
                        <span className={styles.contactHours}>{contact.hours}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}